// Light / dark UI theme. The preference ('auto' | 'light' | 'dark') lives in
// localStorage; 'auto' follows prefers-color-scheme. The resolved theme is the
// `data-theme` attribute on <html>, which index.html's CSS tokens key off. An
// inline script in <head> applies the same rule before first paint, so the
// logic here must stay in step with it.
import { PREF, safeGet, safeSet } from '../state/prefs.ts';
import { $ } from './dom.ts';

export type ThemePref = 'auto' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

export const THEME_COLOR: Record<Theme, string> = { light: '#ffffff', dark: '#1b1f24' };

/** Canvas background a fresh view gets under each theme (user choice overrides). */
export function themeCanvasBg(t: Theme): string { return t === 'dark' ? '#080a0e' : '#ffffff'; }

export interface ThemeController {
  current(): Theme;
  /** Called after the resolved theme changes (button or system), with the previous theme. */
  onChange: ((theme: Theme, prev: Theme) => void) | null;
}

export function parseThemePref(v: string | null | undefined): ThemePref {
  return v === 'light' || v === 'dark' ? v : 'auto';
}

export function resolveTheme(pref: ThemePref, systemDark: boolean): Theme {
  if (pref === 'auto') return systemDark ? 'dark' : 'light';
  return pref;
}

/** Button cycle: follow system -> light -> dark -> follow system. */
export function nextThemePref(p: ThemePref): ThemePref {
  return p === 'auto' ? 'light' : p === 'light' ? 'dark' : 'auto';
}

export function themeLabel(pref: ThemePref, resolved: Theme): { glyph: string; text: string; title: string } {
  if (pref === 'auto') return { glyph: '◐', text: 'Auto', title: `Theme follows your system (currently ${resolved}). Click for light.` };
  if (pref === 'light') return { glyph: '☀', text: 'Light', title: 'Light theme. Click for dark.' };
  return { glyph: '☾', text: 'Dark', title: 'Dark theme. Click to follow the system.' };
}

export function mountTheme(): ThemeController {
  const btn = $<HTMLButtonElement>('btnTheme');
  const mq = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
  let pref = parseThemePref(safeGet(PREF.theme, 'auto'));
  let resolved: Theme = resolveTheme(pref, !!mq?.matches);
  const ctl: ThemeController = { current: () => resolved, onChange: null };

  const apply = () => {
    const t = resolveTheme(pref, !!mq?.matches);
    const prev = resolved;
    resolved = t;
    document.documentElement.dataset.theme = t;
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[t]);
    const l = themeLabel(pref, t);
    const glyph = btn.querySelector('.glyph'), label = btn.querySelector('.button-label');
    if (glyph && label) { glyph.textContent = l.glyph; label.textContent = l.text; }
    else btn.textContent = l.glyph + ' ' + l.text;
    btn.title = l.title;
    btn.setAttribute('aria-label', l.title);
    if (t !== prev) ctl.onChange?.(t, prev);
  };
  mq?.addEventListener('change', apply);
  btn.addEventListener('click', () => {
    pref = nextThemePref(pref);
    safeSet(PREF.theme, pref);
    apply();
  });
  apply();
  return ctl;
}
